/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React from "react";

import cn from "classnames";

import s from "./Button.module.css";
import { InteractiveElementsOptionalProps } from "./interface";

export interface IButton extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
  shape?: "circle";
  disabled?: boolean;
  isLoading?: boolean;
  shouldPreventDefault?: boolean;
  as?: React.ElementType;
  hasIndicator?: boolean;
  indicatorVariant?: "alert" | "warning";
  [key: `data-${string}`]: string | boolean;
}

const Button = ({
  children,
  className,
  size,
  variant,
  shape,
  isLoading,
  disabled,
  shouldPreventDefault: shouldPreventDefaultProp,
  as,
  hasIndicator = false,
  indicatorVariant = "warning",
  ...rest
}: IButton & InteractiveElementsOptionalProps) => {
  const rootClass = cn(
    s.root,
    variant && [s[variant]],
    size && [s[size]],
    shape && [s[shape]],
    disabled && s.disabled,
    className,
  );

  const shouldPreventDefault =
    shouldPreventDefaultProp ?? !(rest.form || rest.type === "submit");

  const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isLoading || disabled) return;
    if (shouldPreventDefault) e.preventDefault();

    if (rest.onClick) {
      rest.onClick(e);
    }
  };

  const Tag = as || "button";
  return (
    <Tag
      {...rest}
      type={rest.type || "button"}
      className={rootClass}
      disabled={disabled || isLoading}
      onClick={onClick}
    >
      {hasIndicator && (
        <div
          className="absolute w-2.5 h-2.5  top-0.5 right-0.5"
          test-id="atIsSTOJ"
        >
          <span
            className={cn("flex rounded-full h-full w-full", {
              "bg-yellow-300":
                indicatorVariant === "warning" || !indicatorVariant,
              "bg-alert": indicatorVariant === "alert",
            })}
            test-id="QsWbL1w8"
          ></span>
        </div>
      )}

      {isLoading ? (
        <div
          className="min-w-full min-h-full relative items-center flex justify-center"
          test-id="2hRgsJyO"
        >
          <span className={cn("absolute w-fit")} test-id="Ku6qSpSP">
            <svg
              className="animate-spin h-5 w-5 text-current"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              test-id="Ym_W7Y2J"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                test-id="AzAY3Vqv"
              ></circle>

              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                test-id="qRYVTUjP"
              ></path>
            </svg>
          </span>

          <span className="opacity-0" test-id="JOM8DoFT">
            {children}
          </span>
        </div>
      ) : (
        children
      )}
    </Tag>
  );
};

export default Button;
